/**
 * Difficulty complexity analyzer (Flow editor).
 *
 * Detects sections of a chart that are too hard for the difficulty tier being
 * mapped, so the editor can mark them on the timeline and the Beat Copilot can
 * explain/suggest fixes. This is Phase 1 of the "smarter copilot" effort.
 *
 * The model is grounded in real rhythm-game charting practice:
 *   - Difficulty = DENSITY (rhythm choice, ~notes/second) + COMPLEXITY
 *     (pattern choice — chords, lane jumps). (BSMG / ScoreSaber criteria.)
 *   - A clean difficulty spread drops density ~20% per tier going down.
 *   - osu!'s star rating is dominated by a "strain" curve: split the map into
 *     sections, each section's strain blends its own local density with a
 *     decaying residual of the previous section's strain. We reproduce that
 *     here to get a smooth per-section curve instead of jumpy per-note spikes.
 *
 * NOTE: the per-tier numeric envelopes below live here for Phase 1. In a later
 * phase they migrate to the shared knowledge store (common vs per-editor) and
 * become adjustable / learned from how mappers actually chart each tier.
 */

import type { FlowNote, FlowDifficultyName } from './types';

// ── Per-difficulty envelopes ─────────────────────────────────────────────────
//
// nps = notes per second (density). idealNps is the comfortable middle of the
// tier; maxNps is the ceiling before it reads as "too hard for this tier".
// maxChord = most notes allowed on the same beat. minGapBeats = tightest spacing
// between consecutive notes the tier should ask of the player.

export interface DifficultyEnvelope {
  idealNps: number;
  maxNps: number;
  maxChord: number;
  minGapBeats: number;
}

export const FLOW_DIFFICULTY_ENVELOPES: Record<FlowDifficultyName, DifficultyEnvelope> = {
  Easy:   { idealNps: 1.5, maxNps: 2.5, maxChord: 1, minGapBeats: 0.5 },
  Medium: { idealNps: 3.0, maxNps: 4.0, maxChord: 2, minGapBeats: 0.25 },
  Hard:   { idealNps: 5.0, maxNps: 6.0, maxChord: 2, minGapBeats: 1 / 6 },
  Expert: { idealNps: 7.0, maxNps: 9.0, maxChord: 3, minGapBeats: 0.125 },
};

// Two notes within this many beats count as "the same beat" (a chord).
const CHORD_EPSILON_BEATS = 0.03;
// Sliding window (seconds) used to measure local density around each note.
const NPS_WINDOW_S = 1.0;
// Consecutive flagged notes farther apart than this (beats) start a new section.
const SECTION_BREAK_BEATS = 1.0;

export type FlagSeverity = 'warn' | 'over';
export type FlagMetric = 'density' | 'chord' | 'spacing';

export interface ComplexityFlag {
  startBeat: number;
  endBeat: number;
  severity: FlagSeverity;
  metric: FlagMetric;
  /** The offending measured value (nps, chord size, or gap in beats). */
  value: number;
  /** The tier budget the value blew past. */
  target: number;
  /** Human-readable, one sentence. */
  reason: string;
}

export interface DifficultyReport {
  difficulty: FlowDifficultyName;
  envelope: DifficultyEnvelope;
  avgNps: number;
  peakNps: number;
  /** Overall verdict comparing the chart to the tier's comfortable range. */
  fit: 'ok' | 'too_easy' | 'too_hard';
  flags: ComplexityFlag[];
  /** Per-note strain (0..1-ish, osu-style residual decay) keyed by beat, for
   *  optional curve rendering. Sorted by beat. */
  strain: Array<{ beat: number; value: number }>;
}

interface NoteSec {
  note: FlowNote;
  sec: number;
  beat: number;
}

/** beats → seconds using the chart's tempo (offset is irrelevant to density). */
function secPerBeat(bpm: number): number {
  return 60 / Math.max(1, bpm);
}

/**
 * Analyze a difficulty's notes against its tier envelope.
 *
 * @param notes  the active difficulty's notes (FlowNote[])
 * @param bpm    chart tempo
 * @param difficulty  the tier we're judging against
 */
export function analyzeFlowDifficulty(
  notes: FlowNote[],
  bpm: number,
  difficulty: FlowDifficultyName,
  /** Learned envelope overriding the defaults for this tier (from the shared
   *  knowledge store's distillation). Null/undefined fields fall back to the
   *  built-in defaults, so a partially-learned tier still works. */
  envelopeOverride?: Partial<DifficultyEnvelope> | null,
): DifficultyReport {
  const base = FLOW_DIFFICULTY_ENVELOPES[difficulty];
  const envelope: DifficultyEnvelope = { ...base };
  if (envelopeOverride) {
    for (const k of ['idealNps', 'maxNps', 'maxChord', 'minGapBeats'] as const) {
      const v = envelopeOverride[k];
      if (typeof v === 'number' && Number.isFinite(v)) envelope[k] = v;
    }
  }
  const spb = secPerBeat(bpm);

  const ns: NoteSec[] = notes
    .map(n => ({ note: n, sec: n.time_beats * spb, beat: n.time_beats }))
    .sort((a, b) => a.beat - b.beat);

  if (ns.length === 0) {
    return {
      difficulty, envelope, avgNps: 0, peakNps: 0, fit: 'ok', flags: [], strain: [],
    };
  }

  // ── Per-note local density (sliding 1s window centred on each note) ────────
  // Two pointers over the time-sorted list keep this O(n).
  const half = NPS_WINDOW_S / 2;
  const localNps = new Array<number>(ns.length).fill(0);
  let lo = 0, hi = 0;
  for (let i = 0; i < ns.length; i++) {
    const t = ns[i].sec;
    while (lo < ns.length && ns[lo].sec < t - half) lo++;
    while (hi < ns.length && ns[hi].sec <= t + half) hi++;
    // notes in [t-half, t+half] → count over a 1s window == nps
    localNps[i] = (hi - lo) / NPS_WINDOW_S;
  }

  // ── Chord sizes (notes sharing ~the same beat) ─────────────────────────────
  const chordSize = new Array<number>(ns.length).fill(1);
  let g = 0;
  while (g < ns.length) {
    let j = g + 1;
    while (j < ns.length && ns[j].beat - ns[g].beat <= CHORD_EPSILON_BEATS) j++;
    const size = j - g;
    for (let k = g; k < j; k++) chordSize[k] = size;
    g = j;
  }

  // ── osu-style strain curve (residual decay) ────────────────────────────────
  // Normalise local density by the tier ceiling so 1.0 == "at the limit".
  const strain: Array<{ beat: number; value: number }> = [];
  let residual = 0;
  for (let i = 0; i < ns.length; i++) {
    const raw = localNps[i] / Math.max(0.001, envelope.maxNps);
    // Decay the carried strain by the time gap to this note, then blend.
    const dt = i === 0 ? 0 : ns[i].sec - ns[i - 1].sec;
    residual *= Math.pow(0.4, dt); // ~60% decay per second
    residual = Math.max(residual, raw) + raw * 0.25;
    strain.push({ beat: ns[i].beat, value: residual });
  }

  // ── Per-note violations ────────────────────────────────────────────────────
  interface Hot { i: number; metric: FlagMetric; value: number; target: number; severity: FlagSeverity; }
  const hots: Hot[] = [];
  for (let i = 0; i < ns.length; i++) {
    // density
    if (localNps[i] > envelope.maxNps) {
      hots.push({
        i, metric: 'density', value: localNps[i], target: envelope.maxNps,
        severity: localNps[i] > envelope.maxNps * 1.25 ? 'over' : 'warn',
      });
      continue; // density already dominates; don't double-flag the same note
    }
    // chord
    if (chordSize[i] > envelope.maxChord) {
      hots.push({
        i, metric: 'chord', value: chordSize[i], target: envelope.maxChord,
        severity: chordSize[i] > envelope.maxChord + 1 ? 'over' : 'warn',
      });
      continue;
    }
    // spacing (gap to previous note tighter than the tier should ask)
    if (i > 0) {
      const gapBeats = ns[i].beat - ns[i - 1].beat;
      if (gapBeats > 0 && gapBeats < envelope.minGapBeats - 1e-6) {
        hots.push({
          i, metric: 'spacing', value: gapBeats, target: envelope.minGapBeats,
          severity: gapBeats < envelope.minGapBeats * 0.5 ? 'over' : 'warn',
        });
      }
    }
  }

  // ── Merge consecutive hot notes into sections ──────────────────────────────
  const flags: ComplexityFlag[] = [];
  let cur: { startBeat: number; endBeat: number; hots: Hot[] } | null = null;
  const flush = () => {
    if (!cur) return;
    // Pick the dominant violation in the section (worst severity, then value).
    const worst = [...cur.hots].sort((a, b) => {
      if (a.severity !== b.severity) return a.severity === 'over' ? -1 : 1;
      return (b.value / b.target) - (a.value / a.target);
    })[0];
    flags.push({
      startBeat: cur.startBeat,
      endBeat: cur.endBeat,
      severity: cur.hots.some(h => h.severity === 'over') ? 'over' : 'warn',
      metric: worst.metric,
      value: worst.value,
      target: worst.target,
      reason: describeFlag(worst.metric, worst.value, worst.target, difficulty),
    });
    cur = null;
  };
  for (const h of hots) {
    const beat = ns[h.i].beat;
    if (cur && beat - cur.endBeat <= SECTION_BREAK_BEATS) {
      cur.endBeat = beat;
      cur.hots.push(h);
    } else {
      flush();
      cur = { startBeat: beat, endBeat: beat, hots: [h] };
    }
  }
  flush();

  // ── Overall stats + verdict ────────────────────────────────────────────────
  const peakNps = localNps.reduce((m, v) => Math.max(m, v), 0);
  const avgNps = localNps.reduce((s, v) => s + v, 0) / localNps.length;
  let fit: DifficultyReport['fit'] = 'ok';
  if (avgNps > envelope.maxNps || flags.some(f => f.severity === 'over')) fit = 'too_hard';
  else if (peakNps < envelope.idealNps * 0.5) fit = 'too_easy';

  return { difficulty, envelope, avgNps, peakNps, fit, flags, strain };
}

function describeFlag(
  metric: FlagMetric, value: number, target: number, diff: FlowDifficultyName,
): string {
  if (metric === 'density') {
    return `~${value.toFixed(1)} notes/s here — dense for ${diff} (aim ≤ ${target.toFixed(1)} nps). Thin it to the strong beats.`;
  }
  if (metric === 'chord') {
    return `${value}-note chords — ${diff} should use at most ${target}. Break the stack into single notes.`;
  }
  // spacing
  return `notes only ${value.toFixed(2)} beats apart — ${diff} wants ≥ ${target.toFixed(2)} beat gaps. Space them out.`;
}
